import React, { lazy, Suspense } from "react";
import { Modal } from "react-bootstrap";
import PanZoomAsset from "./PanZoomAsset";

const ProductModelViewer = lazy(() => import("./ProductModelViewer"));

/**
 * 渲染单个工程资源卡片的统一标题和内容容器。
 * @param {{ title: string, children: React.ReactNode }} props 组件属性
 * @returns {React.JSX.Element} 工程资源卡片
 */
const AssetCard = ({ title, children }) => (
  <section className="min-w-0 overflow-hidden rounded-lg border border-gray-200 bg-white">
    <h3 className="border-b border-gray-100 px-4 py-3 text-sm font-semibold text-gray-800">
      {title}
    </h3>
    {children}
  </section>
);

/**
 * 展示商品 PDF、引脚图、焊盘图和三维模型的数据手册弹窗。
 * @param {{ show: boolean, onHide: () => void, product?: Record<string, any> }} props 组件属性
 * @returns {React.JSX.Element} 数据手册弹窗
 */
const EngineeringAssetsDialog = ({ show, onHide, product }) => {
  const hasEngineeringAssets = Boolean(
    product?.pinoutImage || product?.footprintImage || product?.model3dPreview,
  );

  return (
    <Modal show={show} onHide={onHide} size="xl" centered scrollable aria-labelledby="engineering-assets-title">
      <Modal.Header closeButton>
        <Modal.Title id="engineering-assets-title" className="text-lg font-bold">
          {product?.CTYEA_Part_Number || product?.Manufacturer_Part_Number || "Product"} Datasheet
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="bg-[#f5f8fc]">
        <div className="mb-4 flex flex-col gap-3 rounded-lg bg-white p-4 md:flex-row md:items-center md:justify-between">
          <div className="min-w-0">
            <p className="mb-1 text-sm font-semibold text-gray-800">
              {product?.Manufacturer_Part_Number || product?.name}
            </p>
            <p className="mb-0 text-xs text-gray-500">PDF, pinout, footprint, and 3D engineering resources</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {product?.datasheet && (
              <a href={product.datasheet} target="_blank" rel="noreferrer" className="rounded border border-blue-500 px-3 py-2 text-sm text-blue-600 no-underline hover:bg-blue-50">
                View PDF
              </a>
            )}
            {product?.model3dSource && (
              <a href={product.model3dSource} target="_blank" rel="noreferrer" className="rounded bg-blue-600 px-3 py-2 text-sm text-white no-underline hover:bg-blue-700">
                Download STEP/STP
              </a>
            )}
          </div>
        </div>

        {hasEngineeringAssets ? (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <AssetCard title="PINOUT">
              <PanZoomAsset src={product?.pinoutImage} alt={`${product?.name || "Product"} pinout`} />
            </AssetCard>
            <AssetCard title="FOOTPRINT">
              <PanZoomAsset src={product?.footprintImage} alt={`${product?.name || "Product"} footprint`} dark />
            </AssetCard>
            <AssetCard title="3D MODEL">
              {product?.model3dPreview ? (
                <Suspense fallback={<div className="flex h-[320px] items-center justify-center text-sm text-gray-500">Loading 3D viewer…</div>}>
                  <ProductModelViewer src={product.model3dPreview} />
                </Suspense>
              ) : (
                <div className="flex h-[320px] items-center justify-center px-6 text-center text-sm text-gray-500">
                  No GLB preview model available
                </div>
              )}
            </AssetCard>
          </div>
        ) : (
          <div className="rounded-lg border border-dashed border-gray-300 bg-white p-10 text-center text-sm text-gray-500">
            No pinout, footprint, or 3D preview model is available for this product.
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default EngineeringAssetsDialog;
